import { Instagram, MessageCircle, Globe, Truck } from "lucide-react";
import { Link } from "react-router-dom";

const TRACK_LABEL = { en: "Track my order", fr: "Suivre ma commande", ar: "تتبع طلبي", bn: "আমার অর্ডার ট্র্যাক করুন" };
import { CONTACT, IMAGES } from "../data/products";
import { Reveal } from "./Reveal";
import { useLang } from "../i18n";

export const Footer = () => {
    const { t, lang } = useLang();
    const houseItems = t("footer.houseItems");
    return (
        <footer className="bg-ivory border-t border-gold/25 py-24 px-6 sm:px-10" data-testid="site-footer">
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-16 items-start">
                <Reveal>
                    <img src={IMAGES.logo} alt="Al Zahra Perfumery logo" className="w-28 mix-blend-multiply" data-testid="footer-logo" />
                    <p className="font-serif italic text-xl text-smoke mt-6 max-w-xs leading-relaxed">{t("footer.quote")}</p>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="text-xs tracking-[0.3em] uppercase text-golddark mb-8">{t("footer.reach")}</p>
                    <div className="space-y-5">
                        <a
                            data-testid="footer-whatsapp-link"
                            href={`https://wa.me/${CONTACT.whatsapp}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-4 text-smoke hover:text-golddark transition-colors duration-300 group"
                        >
                            <MessageCircle size={17} strokeWidth={1.5} className="text-gold" />
                            <span className="text-sm tracking-wide border-b border-transparent group-hover:border-gold transition-colors duration-300">
                                {t("footer.wa")} — {CONTACT.whatsappDisplay}
                            </span>
                        </a>
                        <a
                            data-testid="footer-instagram-link"
                            href={`https://www.instagram.com/${CONTACT.instagram}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-4 text-smoke hover:text-golddark transition-colors duration-300 group"
                        >
                            <Instagram size={17} strokeWidth={1.5} className="text-gold" />
                            <span className="text-sm tracking-wide border-b border-transparent group-hover:border-gold transition-colors duration-300">
                                {t("footer.ig")} — @{CONTACT.instagram}
                            </span>
                        </a>
                        <a
                            data-testid="footer-website-link"
                            href={`https://${CONTACT.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-4 text-smoke hover:text-golddark transition-colors duration-300 group"
                        >
                            <Globe size={17} strokeWidth={1.5} className="text-gold" />
                            <span className="text-sm tracking-wide border-b border-transparent group-hover:border-gold transition-colors duration-300">
                                {CONTACT.website}
                            </span>
                        </a>
                        <p className="text-xs text-ash tracking-wide pt-2">{t("footer.owner")}</p>
                        <Link to="/track" data-testid="footer-track-link" className="flex items-center gap-4 text-smoke hover:text-golddark transition-colors duration-300 group pt-2">
                            <Truck size={17} strokeWidth={1.5} className="text-gold" />
                            <span className="text-sm tracking-wide border-b border-transparent group-hover:border-gold transition-colors duration-300">
                                {TRACK_LABEL[lang] ?? TRACK_LABEL.en}
                            </span>
                        </Link>
                    </div>
                </Reveal>
                <Reveal delay={0.2}>
                    <p className="text-xs tracking-[0.3em] uppercase text-golddark mb-8">{t("footer.house")}</p>
                    <ul className="space-y-4 text-sm text-smoke">
                        {houseItems.map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>
                </Reveal>
            </div>
            <nav className="max-w-7xl mx-auto mt-16 flex flex-wrap gap-x-8 gap-y-3" data-testid="footer-info-links">
                {[["/shop", "Shop"], ["/about", "About"], ["/contact", "Contact"], ["/shipping-policy", "Shipping"], ["/refund-policy", "Refunds & Returns"], ["/privacy", "Privacy"], ["/terms", "Terms"]].map(([to, label]) => (
                    <Link key={to} to={to} data-testid={`footer-link-${to.slice(1)}`} className="text-[10px] tracking-[0.25em] uppercase text-smoke hover:text-golddark transition-colors duration-300">{label}</Link>
                ))}
            </nav>
            <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-gold/15 flex flex-col sm:flex-row justify-between gap-3">
                <span className="text-[10px] tracking-[0.25em] uppercase text-ash">{t("footer.rights")}</span>
                <a href={`https://${CONTACT.website}`} className="text-[10px] tracking-[0.25em] text-golddark hover:text-charcoal transition-colors duration-300" data-testid="footer-domain">
                    {CONTACT.website}
                </a>
                <span className="text-[10px] tracking-[0.25em] uppercase text-ash">{t("footer.crafted")}</span>
                <Link to="/admin" className="text-[10px] tracking-[0.25em] uppercase text-ash/60 hover:text-golddark transition-colors duration-300" data-testid="footer-admin-link">
                    Maison Login
                </Link>
            </div>
        </footer>
    );
};
